import React, { useState, useEffect } from "react"
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  Label,
} from "reactstrap"
import paginationFactory, {
  PaginationListStandalone,
  PaginationProvider,
} from "react-bootstrap-table2-paginator"
import ToolkitProvider from "react-bootstrap-table2-toolkit"
import BootstrapTable from "react-bootstrap-table-next"
import toastr from "toastr"
import axios from "axios"
import { API_URL, imagePath } from "constants/constants"
import { hasPermission, showErrorMessages } from "helpers/helpers"
import { storeNotesData } from "helpers/repository"
import { showMessage } from "components/Common/Toaster"

function KYCTable(props) {
  const { data: initialData } = props
  const [data, setData] = useState([])
  const [showModal, setShowModal] = useState(false)
  const [imageUrl, setImageUrl] = useState("")
  const [modalShow, setModalShow] = useState(false)
  const [issue, setIssue] = useState("")
  const [note, setNote] = useState("")
  const [resolution, setResolution] = useState("")
  const [currentId, setCurrentId] = useState(null)

  useEffect(() => {
    setData(initialData)
  }, [initialData])

  const handleStatusChange = async (id, status) => {
    if (status == 1) {
      try {
        const response = await axios.post(
          `${API_URL}/admin/${id}/update-status/${status}`
        )

        toastr.success(response.data.message)
        props.call()
      } catch (error) {
        toastr.error("Error updating status")
      }
    } else {
      setCurrentId(id)
      setModalShow(true)
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()
    try {
      const obj = JSON.parse(localStorage.getItem("user"))
      const data = {
        note_by: obj.user.id,
        note_to: currentId,
        note: note,
        issue: issue,
        resolution: resolution,
      }

      await storeNotesData(data)
      showMessage("success", "Note created successfully")

      const response = await axios.post(
        `${API_URL}/admin/${currentId}/update-status/2`
      )

      toastr.success(response.data.message)
      props.call()
      setIssue("")
      setNote("")
      setResolution("")
      setModalShow(false)
    } catch (error) {
      console.error("Error in handleSubmit:", error)
      showErrorMessages(error)
    }
  }

  const downloadClick = async (event, id) => {
    event.preventDefault()
    try {
      const response = await axios.get(
        `${API_URL}/admin/download-image/${id}`,
        {
          responseType: "blob",
        }
      )
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", id)
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (error) {
      console.error("Error downloading the file:", error)
    }
  }

  const handleImageClick = url => {
    setImageUrl(url)
    setShowModal(true)
  }

  const toggle = () => {
    setModalShow(false)
  }

  const Columns = [
    {
      dataField: "id",
      text: "User ID",
      sort: true,
    },
    {
      dataField: "phone_number",
      text: "Phone Number",
      sort: true,
    },
    {
      dataField: "nrc_number",
      text: "NRC Number",
      sort: true,
    },
    {
      dataField: "id_card",
      text: "ID Card",
      formatter: (cell, row) => (
        <div
          style={{
            display: "flex",
            justifyContent: "start",
            alignItems: "center",
            gap: "2px",
          }}
        >
          <span>{row.id_card}</span>
          {hasPermission("view-idcard") && (
            <Button
              color="link"
              onClick={() => handleImageClick(imagePath("idCard", row.id_card))}
            >
              <i className="mdi mdi-eye font-size-18" />
            </Button>
          )}
          {hasPermission("download-idcard") && (
            <a
              className="text-dark"
              onClick={e => downloadClick(e, row.id_card)}
            >
              <i className="bx bx-download h3 m-0"></i>
            </a>
          )}
        </div>
      ),
    },
    {
      dataField: "p_status",
      text: "Status",
      sort: true,
      formatter: cell => (
        <>
          {cell == 0 ? (
            <span className="bg-danger font-size-10 badge bg-secondary">
              Not Verified
            </span>
          ) : (
            <span className="bg-success font-size-10 badge bg-secondary">
              Verified
            </span>
          )}
        </>
      ),
    },
    {
      dataField: "created_at",
      text: "Date",
      sort: true,
      formatter: (cell, row) => {
        const date = new Date(row?.created_at)
        const formattedDate = date.toLocaleString("en-GB", {
          timeZone: "CAT",
          hour12: true,
        })
        const capitalizedDate = formattedDate.replace(/(am|pm)/gi, match =>
          match.toUpperCase()
        )
        return <>{capitalizedDate}</>
      },
    },
    {
      dataField: "duplicate_nrc",
      text: "Duplicate NRC",
      sort: true,
      formatter: cell => (
        <>
          {cell == 1 ? (
            <span className="bg-danger font-size-10 badge bg-secondary">
              duplicate
            </span>
          ) : (
            ""
          )}
        </>
      ),
    },
    {
      dataField: "actions",
      text: "Action",
      sort: true,
      formatter: (cellContent, row) => (
        <div className="btn-group" role="group" aria-label="Status toggle">
          <button
            type="button"
            className={`btn btn-sm btn-success me-2`}
            onClick={() => handleStatusChange(row.id, 1)}
          >
            Approve
          </button>

          <button
            type="button"
            className={`btn btn-sm btn-danger`}
            onClick={() => handleStatusChange(row.id, 2)}
          >
            Decline
          </button>
        </div>
      ),
    },
  ]

  const defaultSorted = [
    {
      dataField: "id",
      order: "desc",
    },
  ]

  const pageOptions = {
    sizePerPage: 10,
    totalSize: data.length,
    custom: true,
  }

  return (
    <Card>
      <CardBody>
        <div className="mb-4 h4 card-title">{"KYC"}</div>
        <PaginationProvider
          pagination={paginationFactory(pageOptions)}
          keyField="id"
          columns={Columns}
          data={data}
        >
          {({ paginationProps, paginationTableProps }) => (
            <ToolkitProvider keyField="id" data={data} columns={Columns}>
              {toolkitProps => (
                <>
                  <Row>
                    <Col xl="12">
                      <div className="table-responsive">
                        <BootstrapTable
                          keyField="id"
                          responsive
                          bordered={false}
                          striped={false}
                          defaultSorted={defaultSorted}
                          classes={
                            "table align-middle table-nowrap table-check"
                          }
                          headerWrapperClasses={"table-light"}
                          {...toolkitProps.baseProps}
                          {...paginationTableProps}
                          rowClasses={"mytable-hover-row"}
                        />
                      </div>
                    </Col>
                  </Row>
                  <div className="pagination pagination-rounded justify-content-end">
                    <PaginationListStandalone {...paginationProps} />
                  </div>
                </>
              )}
            </ToolkitProvider>
          )}
        </PaginationProvider>

        {/* Image Modal */}
        <Modal isOpen={showModal} toggle={() => setShowModal(false)} size="lg">
          <ModalHeader toggle={() => setShowModal(false)}>ID Card</ModalHeader>
          <ModalBody>
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                src={imageUrl}
                alt="ID Card"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            </div>
          </ModalBody>
        </Modal>

        {/* {Notes decline modal} */}
        <Modal isOpen={modalShow} toggle={toggle} centered>
          <ModalHeader toggle={toggle} tag="h4">
            {"Add new Note"}
          </ModalHeader>
          <ModalBody>
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <Label htmlFor="issue" className="form-label">
                  Issue
                </Label>
                <select
                  className="form-control"
                  name="issue"
                  value={issue}
                  onChange={e => setIssue(e.target.value)}
                >
                  <option value="">Select Issue</option>
                  <option value="unclear_image">Unclear image</option>
                  <option value="nrc_does_not_match">NRC does not match</option>
                  <option value="name_and_surname_dont_match">
                    Name and Surname dont match
                  </option>
                  <option value="duplicate_NRC_found">
                    Duplicate NRC found
                  </option>
                </select>
              </div>
              <div className="mb-3">
                <Label htmlFor="note" className="form-label">
                  Note
                </Label>
                <input
                  type="text"
                  placeholder="Note"
                  className="form-control"
                  value={note}
                  onChange={e => setNote(e.target.value)}
                />
              </div>
              <div className="mb-3">
                <Label htmlFor="resolution" className="form-label">
                  Resolution
                </Label>
                <select
                  className="form-control"
                  name="resolution"
                  value={resolution}
                  onChange={e => setResolution(e.target.value)}
                >
                  <option value="">Select Resolution</option>
                  <option value="declined">Declined</option>
                </select>
              </div>
              <div className="row">
                <Col sm={6} xs={6} md={6} lg={6} xl={6}>
                  <Button type="submit" color="primary" className="w-100">
                    Submit
                  </Button>
                </Col>
                <Col sm={6} xs={6} md={6} lg={6} xl={6}>
                  <Button
                    type="button"
                    color="secondary"
                    className="w-100"
                    onClick={toggle}
                  >
                    Cancel
                  </Button>
                </Col>
              </div>
            </form>
          </ModalBody>
        </Modal>
      </CardBody>
    </Card>
  )
}

export default KYCTable
